/**
 * Admin gate wrapper for the UI.
 *
 * On mount, probes the `/query` endpoint for the signed-in user's
 * permissions. If the `admin` operation is in the user's allowed set,
 * renders `children`. Otherwise renders the provided `fallback` (access
 * denied page).
 *
 * This is a PURELY COSMETIC gate — the server enforces the real admin check
 * on every /admin/* request. Bypassing this component gets you to the
 * routes but all backend calls will 403.
 */

import React, { useEffect, useState } from 'react';
import Loader from '@amzn/meridian/loader';
import { hasAdminCapability, probePermissions, PermissionsProbe } from './service/adminApiService';

interface AdminGateProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
}

type GateState =
  | { status: 'checking' }
  | { status: 'allowed'; probe: PermissionsProbe }
  | { status: 'denied' };

export const AdminGate: React.FC<AdminGateProps> = ({ children, fallback }) => {
  const [state, setState] = useState<GateState>({ status: 'checking' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // ``probePermissions`` can now reject (see adminApiService.ts — the
      // previous swallow-all try/catch made the outer cache eviction
      // dead code). Without this try/catch here a transient network
      // failure would leave the gate stuck on its initial ``checking``
      // state forever, showing an infinite spinner. Fail closed to the
      // access-denied fallback instead; the next mount will retry
      // because the outer wrapper has evicted the cache.
      try {
        const probe = await probePermissions();
        if (cancelled) return;
        if (probe && hasAdminCapability(probe)) {
          setState({ status: 'allowed', probe });
        } else {
          setState({ status: 'denied' });
        }
      } catch {
        if (!cancelled) setState({ status: 'denied' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === 'checking') {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '4rem' }}
      >
        <Loader type="circular" size="large" />
      </div>
    );
  }

  if (state.status === 'denied') {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * Lightweight hook for non-gate use cases (e.g., to hide the admin nav
 * link in the main app without blocking rendering). Returns `null` while
 * the probe is in flight so callers can render nothing until it resolves.
 */
export function useAdminCapability(): boolean | null {
  const [hasAdmin, setHasAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Same fail-closed pattern as ``AdminGate``: catch any rejection
      // from ``probePermissions`` and fall back to ``false`` so the
      // admin nav link stays hidden rather than leaving callers
      // waiting on ``null`` indefinitely.
      try {
        const probe = await probePermissions();
        if (cancelled) return;
        setHasAdmin(hasAdminCapability(probe));
      } catch {
        if (!cancelled) setHasAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return hasAdmin;
}
